import React from 'react';
import { Link } from 'react-router-dom';
import { Section, Shimmer } from '@/components/ui';
import { CategoryIcon } from '@/lib/categoryIcons';
import { useSubcategories } from '@/hooks/useCatalog';
import { subcategoryIcon } from '@/lib/subcategoryIcons';


/**
 * "Explore Events" — the browse-by row.
 *
 * This replaces a grid of every *service category* on the platform (plumbers,
 * salons, car washes), which on an events-only storefront was a row of dead
 * buttons. These tiles are admin-configured event sub-categories, and each one
 * opens its own landing page.
 */
// Phones get a swipeable single row (like every ticketing app) instead of a
// 2-column grid of tall tiles that pushed the events three screens down;
// tablets and up keep the even grid.
const ROW =
  'scrollbar-hide -mx-4 flex snap-x scroll-px-4 gap-3 overflow-x-auto px-4 pb-2 pt-1 sm:mx-0 sm:grid sm:grid-cols-3 sm:gap-4 sm:overflow-visible sm:px-0 md:grid-cols-6';
const TILE_W = 'w-[7.25rem] shrink-0 snap-start sm:w-auto';

const ExploreEvents = () => {
  const { browsable, loading } = useSubcategories();

  if (!loading && browsable.length === 0) return null;

  return (
    // Phones get these same sub-categories as the pinned rail under the search
    // bar, so the tile grid would only repeat it; it stays for desktop, which
    // has no rail.
    <Section
      title="Explore Events"
      subtitle="Find your kind of night out"
      className="hidden bg-white md:block"
    >
      {loading ? (
        <div className={ROW}>
          {Array.from({ length: 6 }).map((_, i) => (
            <Shimmer key={i} className={`${TILE_W} h-32 rounded-2xl sm:h-40`} />
          ))}
        </div>
      ) : (
        <div className={ROW}>
          {browsable.map((subcategory, i) => (
            /* The stagger is a CSS animation, not a JS one. A JS-driven
               `initial={{opacity:0}}` leaves the tile invisible whenever the
               animation doesn't run — which is exactly when you least want a
               blank row. `both` holds the final frame. */
            <div
              key={subcategory.id}
              className={`${TILE_W} animate-floatUp`}
              style={{ animationDelay: `${i * 40}ms` }}
            >
              <Link
                to={`/events/${subcategory.slug}`}
                className="lk-focus flex h-32 flex-col items-center justify-center gap-2 rounded-2xl border border-ink-200/70 p-4 text-center shadow-card transition-all duration-200 hover:-translate-y-1 hover:shadow-cardHover active:scale-95 sm:h-40 sm:gap-3"
                style={{
                  background: `linear-gradient(180deg, #ffffff 0%, ${
                    subcategory.backgroundColor || '#f2f4fb'
                  } 190%)`,
                }}
              >
                {/* CategoryIcon falls back to a name-matched glyph on a null
                    or broken src, so admin art → bundled art → glyph. */}
                <CategoryIcon
                  src={subcategoryIcon(subcategory)}
                  name={subcategory.name}
                  className="h-14 w-14 drop-shadow-md sm:h-20 sm:w-20"
                />
                <span className="text-xs font-bold uppercase tracking-wide text-ink-900 sm:text-sm">
                  {subcategory.name}
                </span>
              </Link>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
};

export default ExploreEvents;
